<template>
    <div class="kb-editor-container">
        <!-- Custom Toolbar -->
        <div class="kb-editor-toolbar" v-if="editor">
            <button
                type="button"
                @click="
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                "
                :class="{
                    'is-active': editor.isActive('heading', { level: 1 }),
                }"
                class="toolbar-btn"
            >
                H1
            </button>
            <button
                type="button"
                @click="
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                "
                :class="{
                    'is-active': editor.isActive('heading', { level: 2 }),
                }"
                class="toolbar-btn"
            >
                H2
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleBold().run()"
                :class="{ 'is-active': editor.isActive('bold') }"
                class="toolbar-btn"
            >
                <b>B</b>
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleItalic().run()"
                :class="{ 'is-active': editor.isActive('italic') }"
                class="toolbar-btn"
            >
                <i>I</i>
            </button>
            <button
                type="button"
                @click="setLink"
                :class="{ 'is-active': editor.isActive('link') }"
                class="toolbar-btn flex items-center gap-1"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    ></path>
                </svg>
                Link
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleBulletList().run()"
                :class="{ 'is-active': editor.isActive('bulletList') }"
                class="toolbar-btn"
            >
                List
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleOrderedList().run()"
                :class="{ 'is-active': editor.isActive('orderedList') }"
                class="toolbar-btn"
                title="Numbered List"
            >
                1. List
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleBlockquote().run()"
                :class="{ 'is-active': editor.isActive('blockquote') }"
                class="toolbar-btn"
            >
                Quote
            </button>

            <div class="h-6 w-px bg-gray-300 mx-1 self-center"></div>

            <button
                type="button"
                @click="editor.chain().focus().setTextAlign('left').run()"
                :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
                class="toolbar-btn px-2"
                title="Align Left"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 6h16M4 12h10M4 18h16"
                    />
                </svg>
            </button>
            <button
                type="button"
                @click="editor.chain().focus().setTextAlign('center').run()"
                :class="{
                    'is-active': editor.isActive({ textAlign: 'center' }),
                }"
                class="toolbar-btn px-2"
                title="Align Center"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 6h16M7 12h10M4 18h16"
                    />
                </svg>
            </button>
            <button
                type="button"
                @click="editor.chain().focus().setTextAlign('right').run()"
                :class="{
                    'is-active': editor.isActive({ textAlign: 'right' }),
                }"
                class="toolbar-btn px-2"
                title="Align Right"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 6h16M10 12h10M4 18h16"
                    />
                </svg>
            </button>
            <button
                type="button"
                @click="editor.chain().focus().setTextAlign('justify').run()"
                :class="{
                    'is-active': editor.isActive({ textAlign: 'justify' }),
                }"
                class="toolbar-btn px-2"
                title="Justify"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 6h16M4 12h16M4 18h16"
                    />
                </svg>
            </button>

            <div class="h-6 w-px bg-gray-300 mx-1 self-center"></div>

            <button
                type="button"
                @click="triggerFileUpload('image')"
                class="toolbar-btn flex items-center gap-1"
                :disabled="isUploading"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    ></path>
                </svg>
                Image
            </button>
            <div
                class="
                    flex
                    items-center
                    gap-1
                    border border-gray-200
                    rounded
                    p-1
                "
            >
                <button
                    type="button"
                    @click="triggerFileUpload('video')"
                    class="toolbar-btn flex items-center gap-1"
                    :disabled="isUploading"
                    title="Upload Video File"
                >
                    <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        ></path>
                    </svg>
                    Upload Video
                </button>
                <div class="w-px h-4 bg-gray-300"></div>
                <button
                    type="button"
                    @click="promptVideoUrl"
                    class="toolbar-btn flex items-center gap-1"
                    title="Embed Video from URL"
                >
                    <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        ></path>
                    </svg>
                    Video URL
                </button>
            </div>

            <div class="h-6 w-px bg-gray-300 mx-1 self-center"></div>

            <button
                type="button"
                @click="
                    editor
                        .chain()
                        .focus()
                        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                        .run()
                "
                class="toolbar-btn flex items-center gap-1"
                title="Insert Table"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    ></path>
                </svg>
                Table
            </button>

            <!-- Table Actions (visible only when in a table) -->
            <div
                v-if="editor.isActive('table')"
                class="
                    flex
                    items-center
                    gap-1
                    bg-gray-50
                    rounded
                    px-1
                    ml-1
                    border border-gray-200
                "
            >
                <button
                    type="button"
                    @click="editor.chain().focus().addColumnBefore().run()"
                    class="toolbar-btn px-2 text-xs"
                    title="Add Column Before"
                >
                    Col +
                </button>
                <button
                    type="button"
                    @click="editor.chain().focus().deleteColumn().run()"
                    class="toolbar-btn px-2 text-xs text-red-600"
                    title="Delete Column"
                >
                    Col -
                </button>
                <div class="w-px h-4 bg-gray-300 mx-1"></div>
                <button
                    type="button"
                    @click="editor.chain().focus().addRowAfter().run()"
                    class="toolbar-btn px-2 text-xs"
                    title="Add Row After"
                >
                    Row +
                </button>
                <button
                    type="button"
                    @click="editor.chain().focus().deleteRow().run()"
                    class="toolbar-btn px-2 text-xs text-red-600"
                    title="Delete Row"
                >
                    Row -
                </button>
                <div class="w-px h-4 bg-gray-300 mx-1"></div>
                <button
                    type="button"
                    @click="editor.chain().focus().mergeCells().run()"
                    class="toolbar-btn px-2 text-xs"
                    title="Merge Cells"
                >
                    Merge
                </button>
                <button
                    type="button"
                    @click="editor.chain().focus().splitCell().run()"
                    class="toolbar-btn px-2 text-xs"
                    title="Split Cell"
                >
                    Split
                </button>
                <div class="w-px h-4 bg-gray-300 mx-1"></div>
                <button
                    type="button"
                    @click="editor.chain().focus().deleteTable().run()"
                    class="toolbar-btn px-2 text-xs text-red-600 font-bold"
                    title="Delete Table"
                >
                    Delete Table
                </button>
            </div>

            <div class="h-6 w-px bg-gray-300 mx-1 self-center"></div>

            <button
                type="button"
                @click="refineText"
                class="toolbar-btn flex items-center gap-1 transition-opacity"
                :class="{ 'opacity-50 cursor-not-allowed': !hasSelection }"
                :disabled="isRefining || !hasSelection"
                title="Highlight text to refine with AI"
            >
                <svg
                    v-if="isRefining"
                    class="animate-spin h-4 w-4 text-purple-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
                <span v-else>✨</span>
                <span class="text-purple-700 font-bold">AI Refine</span>
            </button>

            <button
                type="button"
                @click="editor.chain().focus().undo().run()"
                class="toolbar-btn ml-auto"
            >
                Undo
            </button>
            <button
                type="button"
                @click="editor.chain().focus().redo().run()"
                class="toolbar-btn"
            >
                Redo
            </button>
        </div>

        <!-- Hidden File Input -->
        <input
            type="file"
            ref="fileInput"
            class="hidden"
            @change="handleFileUpload"
            :accept="uploadType === 'image' ? 'image/*' : 'video/*'"
        />

        <!-- Uploading Indicator -->
        <div
            v-if="isUploading"
            class="
                bg-blue-50
                px-4
                py-2
                text-sm text-blue-700
                flex
                items-center
                gap-2
                border-b border-blue-100
            "
        >
            <svg
                class="animate-spin h-4 w-4 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                ></circle>
                <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
            </svg>
            Uploading {{ uploadType }}...
        </div>

        <!-- Editor Content -->
        <editor-content :editor="editor" class="kb-editor-content" />

        <!-- Hidden Inputs for Form Submission -->
        <input type="hidden" name="content_tiptap" :value="jsonContent" />
        <input type="hidden" name="content_markdown" :value="textContent" />
    </div>
</template>

<script setup>
import { useEditor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { ResizableImage } from "./forms/tiptap/resizable-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Video } from "./forms/tiptap/video";
import { ref, watch, onMounted } from "vue";
import axios from "axios";

const props = defineProps({
    initialContent: {
        type: [Object, String],
        default: "<p>Start writing your guide here...</p>",
    },
});

const jsonContent = ref("");
const textContent = ref("");
const isUploading = ref(false);
const isRefining = ref(false);
const hasSelection = ref(false);
const uploadType = ref("image");
const fileInput = ref(null);

const editor = useEditor({
    extensions: [
        StarterKit,
        ResizableImage,
        Video,
        Table.configure({
            resizable: true,
            HTMLAttributes: {
                class: "kb-table",
            },
        }),
        TableRow,
        TableHeader,
        TableCell,
        TextAlign.configure({
            types: ["heading", "paragraph", "image"],
        }),
        Link.configure({
            openOnClick: false,
            HTMLAttributes: {
                class: "text-blue-600 hover:underline",
            },
        }),
    ],
    content: props.initialContent,
    onUpdate: ({ editor }) => {
        jsonContent.value = JSON.stringify(editor.getJSON());
        textContent.value = editor.getText();
    },
    onSelectionUpdate: ({ editor }) => {
        const { from, to, empty } = editor.state.selection;
        if (empty) {
            hasSelection.value = false;
        } else {
            const text = editor.state.doc.textBetween(from, to, " ");
            hasSelection.value = text.trim().length > 0;
        }
    },
});

const refineText = async () => {
    if (!editor.value) return;

    const { from, to, empty } = editor.value.state.selection;
    let textToRefine = "";

    if (empty) {
        alert("Please select some text to refine.");
        return;
    } else {
        textToRefine = editor.value.state.doc.textBetween(from, to, " ");
    }

    if (!textToRefine.trim()) return;

    isRefining.value = true;

    try {
        const response = await axios.post("/admin/knowledge-base/refine", {
            text: textToRefine,
        });
        if (response.data.refined_text) {
            editor.value
                .chain()
                .focus()
                .insertContentAt({ from, to }, response.data.refined_text)
                .run();
        }
    } catch (error) {
        console.error("Refine failed", error);
        alert("AI Refinement failed. Please try again.");
    } finally {
        isRefining.value = false;
    }
};

const setLink = () => {
    const previousUrl = editor.value.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl || "");

    // cancelled
    if (url === null) {
        return;
    }

    // empty
    if (url === "") {
        editor.value.chain().focus().extendMarkRange("link").unsetLink().run();

        return;
    }

    // validate basic URL structure to help users
    let finalUrl = url;
    if (
        !finalUrl.startsWith("http://") &&
        !finalUrl.startsWith("https://") &&
        !finalUrl.startsWith("mailto:")
    ) {
        finalUrl = finalUrl;
    }

    // update link
    editor.value
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: finalUrl })
        .run();
};

const promptVideoUrl = () => {
    const url = window.prompt("Enter video URL (e.g., MP4 link):");
    if (url) {
        editor.value
            .chain()
            .focus()
            .insertContent({
                type: "video",
                attrs: {
                    src: url,
                },
            })
            .run();
    }
};

const triggerFileUpload = (type) => {
    uploadType.value = type;
    fileInput.value.click();
};

const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    isUploading.value = true;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", uploadType.value);

    try {
        const response = await axios.post(
            "/admin/knowledge-base/upload",
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );

        if (uploadType.value === "image") {
            editor.value
                .chain()
                .focus()
                .setImage({ src: response.data.url })
                .run();
        } else {
            editor.value
                .chain()
                .focus()
                .insertContent({
                    type: "video",
                    attrs: {
                        src: response.data.url,
                    },
                })
                .run();
        }
    } catch (error) {
        console.error("Upload failed", error);
        alert("Upload failed. Please try again.");
    } finally {
        isUploading.value = false;
        event.target.value = "";
    }
};

// Initialize hidden fields on mount
onMounted(() => {
    if (editor.value) {
        jsonContent.value = JSON.stringify(editor.value.getJSON());
        textContent.value = editor.value.getText();
    }
});
</script>

<style>
/* Standalone TipTap Editor Styling */
.kb-editor-container {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    background: #f9fafb;
    overflow: hidden;
}
.kb-editor-toolbar {
    padding: 0.5rem;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}
.kb-editor-content {
    min-height: 400px;
    background: #fff;
    padding: 1.5rem;
}
.ProseMirror {
    outline: none;
    min-height: 100%;
}
.ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
}
.ProseMirror h1 {
    font-size: 2rem;
    font-weight: 800;
    margin-bottom: 1rem;
}
.ProseMirror h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
}
.ProseMirror p {
    margin-bottom: 1rem;
    line-height: 1.6;
}
.ProseMirror ul,
.ProseMirror ol {
    padding-left: 1.5rem;
    margin-bottom: 1rem;
}
.ProseMirror ul {
    list-style-type: disc;
}
.ProseMirror ol {
    list-style-type: decimal;
}
.ProseMirror li {
    display: list-item;
    margin-bottom: 0.25rem;
}
.ProseMirror li p {
    margin: 0;
}
/* Nested lists shouldn't add extra bottom spacing */
.ProseMirror li > ul,
.ProseMirror li > ol {
    margin-bottom: 0;
    margin-top: 0.25rem;
}
.ProseMirror blockquote {
    border-left: 4px solid #3b82f6;
    padding-left: 1rem;
    color: #4b5563;
    font-style: italic;
}

.resizable-image-container {
    transition: outline 0.2s;
    border-radius: 4px;
}

.resizable-image-container:hover {
    outline: 2px solid #3b82f6;
}

.resize-handle {
    opacity: 0;
    transition: opacity 0.2s;
}

.resizable-image-container:hover .resize-handle {
    opacity: 1;
}

.toolbar-btn {
    padding: 0.4rem 0.6rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    cursor: pointer;
}
.toolbar-btn:hover {
    background: #f3f4f6;
}
.toolbar-btn.is-active {
    background: #dbeafe;
    color: #2563eb;
}

/* Table Styles */
.ProseMirror table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 1rem 0;
    overflow: hidden;
}
.ProseMirror td,
.ProseMirror th {
    min-width: 1em;
    border: 2px solid #ced4da;
    padding: 8px 12px;
    vertical-align: top;
    box-sizing: border-box;
    position: relative;
}
.ProseMirror th {
    font-weight: bold;
    text-align: left;
    background-color: #f1f3f5;
}
.ProseMirror .selectedCell:after {
    z-index: 2;
    position: absolute;
    content: "";
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(200, 200, 255, 0.4);
    pointer-events: none;
}
.ProseMirror .column-resize-handle {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: -2px;
    width: 4px;
    background-color: #adf;
    pointer-events: none;
}
</style>
